// Models
import { GoalStatus } from "../../../service/planner/enumerations/IPlannerEnumerations";
import { GoalExtensionKeys } from "../../../service/planner/extensions/IGoalExtenstionsResource";
import { createIdentitySet } from "../../../utilities/identity";
import { PatchableAndDeltaSyncableODataEntity } from "../common/PatchableAndDeltaSyncableODataEntity";
// Constants
import { GoalConstants } from "@ms/uno-constants/lib/local/GoalConstants";
import { AppContext } from "@ms/uno-constants/lib/local/configuration/AppContext";
// Utilities
import { compareTemporalDate } from "@ms/uno-utilities/lib/local/Comparators";
import cloneDeep from "lodash/cloneDeep";
import { Temporal } from "temporal-polyfill-lite";
import { deserializeNotes, serializeNotes } from "../../../utilities/SerializationUtilities";
export class Goal extends PatchableAndDeltaSyncableODataEntity {
    mapEntityChangesToResource(changed, source) {
        return {
            displayName: changed.displayName,
            extensions: changed.extensions && this.serializeExtensions(changed.extensions),
            finishDate: changed.finishDate && (changed.finishDate?.toString() ?? null),
            notes: changed.notes && serializeNotes(changed.notes),
            commentary: changed.commentary && serializeNotes(changed.commentary),
            initialValue: changed.initialValue,
            currentValue: changed.currentValue,
            targetValue: changed.targetValue,
            aspirationalValue: changed.aspirationalValue,
            priority: changed.priority,
            startDate: changed.startDate && (changed.startDate?.toString() ?? null),
            status: changed.status
        };
    }
    mapDeltaChangesToEntity(delta) {
        return this.processDeltaWithTranslators(delta, {
            archivalInfo: (deltaValue)=>({
                    isArchived: deltaValue.isArchived,
                    justification: deltaValue.justification,
                    modifiedBy: deltaValue.modifiedBy,
                    modifiedDate: deltaValue.modifiedDate ? Temporal.PlainDate.from(deltaValue.modifiedDate) : null
                }),
            extensions: (value)=>Goal.deserializeExtensions(value),
            finishDate: (value)=>value ? Temporal.PlainDate.from(value) : null,
            notes: (value)=>deserializeNotes(value),
            commentary: (value)=>deserializeNotes(value),
            startDate: (value)=>value ? Temporal.PlainDate.from(value) : null,
            createdDateTime: (value)=>value ? Temporal.Instant.from(value) : Temporal.Now.instant()
        });
    }
    static fromResource(resource) {
        return new Goal({
            archivalInfo: {
                isArchived: resource.archivalInfo?.isArchived,
                justification: resource.archivalInfo?.justification,
                modifiedBy: resource.archivalInfo?.modifiedBy ? createIdentitySet(resource.archivalInfo.modifiedBy) : null,
                modifiedDate: resource.archivalInfo?.modifiedDate ? Temporal.PlainDate.from(resource.archivalInfo.modifiedDate) : null
            },
            id: resource.id,
            version: resource["@etag"],
            planId: resource.planId,
            displayName: resource.displayName,
            permissionSetId: resource.permissionSetId,
            priority: resource.priority,
            startDate: resource.startDate ? Temporal.PlainDate.from(resource.startDate) : null,
            finishDate: resource.finishDate ? Temporal.PlainDate.from(resource.finishDate) : null,
            notes: deserializeNotes(resource.notes),
            commentary: deserializeNotes(resource.commentary ?? null),
            initialValue: resource.initialValue ?? null,
            currentValue: resource.currentValue ?? null,
            targetValue: resource.targetValue ?? null,
            aspirationalValue: resource.aspirationalValue ?? null,
            status: resource.status,
            extensions: this.deserializeExtensions(resource.extensions),
            createdBy: createIdentitySet(resource.createdBy),
            createdDateTime: resource.createdDateTime ? Temporal.Instant.from(resource.createdDateTime) : Temporal.Now.instant()
        });
    }
    getClone() {
        return new Goal({
            archivalInfo: cloneDeep(this.archivalInfo),
            id: this.id,
            version: this.version,
            planId: this.planId,
            displayName: this.displayName,
            permissionSetId: this.permissionSetId,
            priority: this.priority,
            startDate: this.startDate ? Temporal.PlainDate.from(this.startDate) : null,
            finishDate: this.finishDate ? Temporal.PlainDate.from(this.finishDate) : null,
            notes: cloneDeep(this.notes),
            commentary: cloneDeep(this.commentary),
            initialValue: this.initialValue,
            currentValue: this.currentValue,
            targetValue: this.targetValue,
            aspirationalValue: this.aspirationalValue,
            status: cloneDeep(this.status),
            extensions: cloneDeep(this.extensions),
            createdBy: cloneDeep(this.createdBy),
            createdDateTime: Temporal.Instant.from(this.createdDateTime)
        });
    }
    forSerialization() {
        return {
            archivalInfo: {
                isArchived: this.archivalInfo?.isArchived,
                justification: this.archivalInfo?.justification,
                modifiedBy: this.archivalInfo?.modifiedBy,
                modifiedDate: this.archivalInfo?.modifiedDate?.toString() ?? null
            },
            id: this.id,
            "@etag": this.version,
            planId: this.planId,
            displayName: this.displayName,
            permissionSetId: this.permissionSetId,
            priority: this.priority,
            startDate: this.startDate?.toString() ?? null,
            finishDate: this.finishDate?.toString() ?? null,
            notes: serializeNotes(this.notes),
            commentary: serializeNotes(this.commentary),
            initialValue: this.initialValue,
            currentValue: this.currentValue,
            targetValue: this.targetValue,
            aspirationalValue: this.aspirationalValue,
            status: this.status,
            extensions: this.serializeExtensions(this.extensions),
            createdBy: this.createdBy,
            createdDateTime: this.createdDateTime.toString()
        };
    }
    forCreate() {
        return {
            planId: this.planId,
            displayName: this.displayName,
            priority: this.priority,
            startDate: this.startDate?.toString() ?? null,
            finishDate: this.finishDate?.toString() ?? null,
            notes: serializeNotes(this.notes),
            // When this flight graduates, make these fields mandatory-but-nullable in IGoalResource.
            ...AppContext.appConfiguration?.flights?.EnableGoalsOKRFields ? {
                commentary: serializeNotes(this.commentary),
                initialValue: this.initialValue,
                currentValue: this.currentValue,
                targetValue: this.targetValue,
                aspirationalValue: this.aspirationalValue
            } : {},
            status: this.status,
            extensions: this.serializeExtensions(this.extensions)
        };
    }
    setProperty(key, value) {
        if (key === "startDate" || key === "finishDate") {
            return this.setDateWithValidation(key, value);
        }
        // Create a clone of the current instance and set the specified property
        const clone = this.getClone();
        clone[key] = value;
        return clone;
    }
    setDateWithValidation(key, value) {
        const resource = this.forSerialization();
        resource[key] = value?.toString() ?? null;
        if (key === "startDate") {
            // If the new start date comes after the finish date, push finish date forward to match
            if (value && this.finishDate && compareTemporalDate(value, this.finishDate) > 0) {
                resource.finishDate = value.toString();
            }
        } else {
            // If the new finish date comes before the start date, pull start date back to match
            if (value && this.startDate && compareTemporalDate(value, this.startDate) < 0) {
                resource.startDate = value.toString();
            }
        }
        return Goal.fromResource(resource);
    }
    /**
     * Serializes goal extensions to a Record<string, string> format for API calls.
     * @param extensions The goal extensions to serialize
     * @returns A Record<string, string> containing the serialized extensions
     */ serializeExtensions(extensions) {
        const result = {};
        const colorData = {
            backgroundColor: extensions.backgroundColor,
            foregroundColor: extensions.foregroundColor
        };
        result[GoalExtensionKeys.colorData] = JSON.stringify(colorData);
        return result;
    }
    /**
     * Deserializes goal extensions from API response format.
     * @param extensions The raw extensions Record from the API
     * @returns The deserialized IGoalExtensions object
     */ static deserializeExtensions(extensions) {
        const result = {
            backgroundColor: "",
            foregroundColor: ""
        };
        const serializedData = extensions[GoalExtensionKeys.colorData];
        if (serializedData) {
            try {
                const colorData = JSON.parse(serializedData);
                result.backgroundColor = colorData.backgroundColor;
                result.foregroundColor = colorData.foregroundColor;
            } catch (parseError) {
                throw new Error(`Deserialization Error: [Entity=Goal][Field=Extensions][Key=colorData]`);
            }
        }
        const copilotData = extensions[GoalExtensionKeys.copilot];
        if (copilotData) {
            try {
                const copilot = JSON.parse(copilotData);
                result.isAIGoal = copilot.isAIGoal;
            } catch  {
            // Malformed or forward-compatible copilot payload — treat as non-AI rather than failing goal deserialization
            }
        }
        return result;
    }
    constructor(propertyBag){
        super(propertyBag);
        this.archivalInfo = {
            isArchived: propertyBag.archivalInfo?.isArchived ?? false,
            justification: propertyBag.archivalInfo?.justification ?? null,
            modifiedBy: propertyBag.archivalInfo?.modifiedBy ?? null,
            modifiedDate: propertyBag.archivalInfo?.modifiedDate ? Temporal.PlainDate.from(propertyBag.archivalInfo.modifiedDate) : null
        };
        this.planId = propertyBag.planId;
        this.displayName = propertyBag.displayName;
        this.permissionSetId = propertyBag.permissionSetId ?? "";
        this.priority = propertyBag.priority ?? GoalConstants.DefaultGoalPriority;
        this.startDate = propertyBag.startDate ?? null;
        this.finishDate = propertyBag.finishDate ?? null;
        this.notes = propertyBag.notes ?? null;
        this.commentary = propertyBag.commentary ?? null;
        this.initialValue = propertyBag.initialValue ?? null;
        this.currentValue = propertyBag.currentValue ?? null;
        this.targetValue = propertyBag.targetValue ?? null;
        this.aspirationalValue = propertyBag.aspirationalValue ?? null;
        this.status = propertyBag.status ?? GoalStatus.NotStarted;
        this.extensions = propertyBag.extensions ?? {
            backgroundColor: "",
            foregroundColor: ""
        };
        this.createdBy = propertyBag.createdBy;
        this.createdDateTime = propertyBag.createdDateTime ?? Temporal.Now.instant();
    }
}
